import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CirclePause from '@gravity-ui/icons/CirclePause';
import Ellipsis from '@gravity-ui/icons/Ellipsis';
import TrashBin from '@gravity-ui/icons/TrashBin';
import { Button, DropdownMenu, Icon, Label, Loader, TextArea, useToaster } from '@gravity-ui/uikit';
import { lengthConfig } from '@paskills/validation';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import {
    ApiError,
    cancelSkillReview,
    deleteApp,
    forceStopApp,
    requestDeploy as requestDeployApi,
    requestSkillReview,
    stopApp,
} from 'client/api';
import { IconMedium } from 'client/blocks/AppIcons/AppIcons';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { OperationStatus } from 'client/blocks/statuses/OperationStatus/OperationStatus';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { FormChangeHandler, FormErrors } from 'client/hocs/types';
import { useIsDraft } from 'client/hooks/useIsDraft';
import { useSnapshotRefresher } from 'client/hooks/useSnapshotRefresher';
import { routes } from 'client/lib/routes';
import { DraftStatus } from 'client/model/draft';
import { AliceSkillFormSettings, InstallableAppFormSettings } from 'client/types';
import { getCanEditApp, getIsAdmin } from 'client/utils';
import { convertToErrors, hasFieldErrors, mapApiErrorsToFormErrors } from 'client/utils/errorMessage';
import * as appFormKeyset from 'i18n/AppForm';
import * as keyset from 'i18n/DraftControlPanel';
import * as errorsKeyset from 'i18n/SettingsErrors';
import * as settingsLabels from 'i18n/SettingsLabels';

import styles from './ControlPanel.module.scss';

type TModal = 'requestReview' | 'cancelReview' | 'changeToDraft' | 'deleteApp' | 'stopApp' | 'forceStopApp';

const CONTROL_PANEL_TOAST = 'control_panel_toast';

const t = i18n(keyset);
const tError = i18n(errorsKeyset);
const tSettings = i18n(settingsLabels);
const tAppForm = i18n(appFormKeyset);

interface ControlPanelProps {
    isSubmitting?: boolean;
    setErrors: (errors: FormErrors<AliceSkillFormSettings & { network: string }>, cb?: () => void) => void;
    handleSaveDraft: () => Promise<void>;
    isNonValidationError: boolean;
    settingValues: AliceSkillFormSettings;
    onSettingValuesChange: FormChangeHandler<AliceSkillFormSettings>;
    settingFormErrors: FormErrors<InstallableAppFormSettings>;
    appName: string;
    logoUrl: string;
}

const convertTypedErrorToMessage = (key: string, value: string) => {
    const typedKey = key as keyof FormErrors<InstallableAppFormSettings & { network?: string }>;
    if (typedKey === 'activationPhrases') {
        return `${tSettings('activation-phrases')}: ${value}`;
    }
    if (typedKey === 'screenshots') {
        return `${tSettings('screenshots')}: ${value}`;
    }
    if (typedKey === 'eulaAgreement') {
        return `${tAppForm('eula-title')}: ${value}`;
    }
    if (typedKey === 'privacyAgreement') {
        return `${tAppForm('private-policy-title')}: ${value}`;
    }
    if (
        typedKey === 'network' &&
        value === 'Name/ActivationPhrases validation error: &apos;Содержит общие фразы&apos;'
    ) {
        return `${tSettings('activation-phrases')}: ${value}`;
    }
    if (typedKey === 'network') {
        return `${tError('network')}: ${value}`;
    }
    return String(value);
};

export const AliceSkillControlPanel: React.FC<ControlPanelProps> = ({
    appName,
    isSubmitting,
    setErrors,
    handleSaveDraft,
    isNonValidationError,
    settingValues,
    onSettingValuesChange,
    settingFormErrors,
    logoUrl,
}) => {
    const [forceStopComment, setForceStopComment] = useState<string>();
    const [openModalType, setOpenModalType] = useState<TModal | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const refreshSnapshot = useSnapshotRefresher();

    const { aliceApp, refreshAliceApp, operations } = useContext(CurrentAliceAppContext)!;
    const { add: addToast } = useToaster();

    const { isDraft, setIsDraft } = useIsDraft(aliceApp.channel, aliceApp.id);
    const navigate = useNavigate();
    const isAdmin = getIsAdmin();

    const disabled = !getCanEditApp(aliceApp);

    useEffect(() => {
        setForceStopComment(undefined);
    }, [aliceApp.id]);

    const formErrors = convertToErrors(settingFormErrors, convertTypedErrorToMessage);
    const hasValidationErrors = hasFieldErrors(settingFormErrors);

    if (isNonValidationError) {
        formErrors.push(tError('unknown-error'));
    }

    const handleErrorMessage = useCallback(
        (error: unknown) => {
            if (error instanceof ApiError && 'fields' in error && !!error.fields) {
                setErrors(error.fields);
                return;
            }

            let errorMessage = tError('network');
            if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                errorMessage = error.message;
            }
            setErrors(mapApiErrorsToFormErrors(errorMessage));
        },
        [setErrors],
    );

    const cancelReview = useCallback(async () => {
        setIsLoading(true);
        try {
            await cancelSkillReview(aliceApp.id, aliceApp.channel);
            refreshAliceApp();
            addToast({ title: t('skill-review-request-success'), theme: 'info', name: CONTROL_PANEL_TOAST });
        } catch (error) {
            handleErrorMessage(error);
            addToast({ title: t('skill-review-request-fail'), theme: 'danger', name: CONTROL_PANEL_TOAST });
        }
        setIsLoading(false);
        setOpenModalType(undefined);
    }, [aliceApp.id, aliceApp.channel, addToast, refreshAliceApp, handleErrorMessage]);

    const requestReview = useCallback(async () => {
        setIsLoading(true);
        try {
            await handleSaveDraft();
            await requestSkillReview(aliceApp.id, aliceApp.channel);
            refreshAliceApp();
            addToast({ title: t('skill-review-request-success'), theme: 'info', name: CONTROL_PANEL_TOAST });
        } catch (error) {
            handleErrorMessage(error);
            addToast({ title: t('skill-review-request-fail'), theme: 'danger', name: CONTROL_PANEL_TOAST });
        }
        setIsLoading(false);
        setOpenModalType(undefined);
    }, [aliceApp.id, aliceApp.channel, addToast, refreshAliceApp, handleSaveDraft, handleErrorMessage]);

    const requestDeploy = useCallback(async () => {
        setIsLoading(true);
        try {
            await requestDeployApi(aliceApp.id, aliceApp.channel);
            refreshAliceApp();
            addToast({ title: t('skill-deploy-request-success'), theme: 'info', name: CONTROL_PANEL_TOAST });
        } catch (error) {
            handleErrorMessage(error);
            addToast({ title: t('skill-deploy-request-fail'), theme: 'danger', name: CONTROL_PANEL_TOAST });
        }
        setIsLoading(false);
    }, [aliceApp.id, aliceApp.channel, addToast, refreshAliceApp, handleErrorMessage]);

    const deleteSkill = useCallback(async () => {
        setIsLoading(true);
        try {
            await deleteApp(aliceApp.id, aliceApp.channel);
            await refreshSnapshot();
            setTimeout(async () => {
                navigate(routes.app.root);
            }, 3000);
            addToast({ title: t('skill-delete-success'), theme: 'info', name: CONTROL_PANEL_TOAST });
        } catch (error) {
            handleErrorMessage(error);
            addToast({ title: t('skill-delete-fail'), theme: 'danger', name: CONTROL_PANEL_TOAST });
        }
        setIsLoading(false);
        setOpenModalType(undefined);
    }, [aliceApp.id, aliceApp.channel, addToast, refreshSnapshot, handleErrorMessage, navigate]);

    const stopSkill = useCallback(async () => {
        setIsLoading(true);
        try {
            await stopApp(aliceApp.id, aliceApp.channel);
            addToast({ title: t('skill-stop-success'), theme: 'info', name: CONTROL_PANEL_TOAST });
            refreshAliceApp();
            setTimeout(() => {
                navigate(routes.app.skillPage(true, 'settings', aliceApp.id));
            }, 1000);
        } catch (error) {
            handleErrorMessage(error);
            addToast({ title: t('skill-stop-fail'), theme: 'danger', name: CONTROL_PANEL_TOAST });
        }
        setIsLoading(false);
    }, [aliceApp.id, aliceApp.channel, addToast, refreshAliceApp, handleErrorMessage, navigate]);

    const forceStopSkill = useCallback(
        async (comment?: string) => {
            if (!comment) return;
            setIsLoading(true);
            try {
                await forceStopApp(aliceApp.id, aliceApp.channel, { comment });
                addToast({ title: t('skill-stop-success'), theme: 'info', name: CONTROL_PANEL_TOAST });
                refreshAliceApp();
                setTimeout(() => {
                    navigate(routes.app.skillPage(true, 'settings', aliceApp.id));
                }, 1000);
            } catch (error) {
                handleErrorMessage(error);
                addToast({ title: t('skill-stop-fail'), theme: 'danger', name: CONTROL_PANEL_TOAST });
            }
            setIsLoading(false);
            setOpenModalType(undefined);
        },
        [aliceApp.id, aliceApp.channel, addToast, refreshAliceApp, handleErrorMessage, navigate],
    );

    if (!operations.length) {
        return (
            <div className={styles.buttonsContainer}>
                <Loader />
            </div>
        );
    }

    let disableModerationButton = !aliceApp.draft.isAllowedForDeploy || (aliceApp.isBanned && !isAdmin);
    const disableStopButton = false;

    let hideSaveButton = true;
    let hideModerationButton = true;
    let hideStopButton = false;
    let hideDeployButton = true;
    let hideChangeButton = false;

    if (isDraft) {
        disableModerationButton = !aliceApp.draft.isAllowedForDeploy || (aliceApp.isBanned && !isAdmin);

        hideSaveButton = aliceApp.draft.status !== DraftStatus['InDevelopment'];
        hideModerationButton = !isDraft || aliceApp.draft.status !== DraftStatus['InDevelopment'];
        hideStopButton = true;
        hideDeployButton = aliceApp.draft.status !== DraftStatus['ReviewApproved'];
        hideChangeButton = aliceApp.draft.status === DraftStatus['InDevelopment'];
    }

    if (disabled) {
        disableModerationButton = true;

        hideSaveButton = true;
        hideModerationButton = true;
        hideStopButton = true;
        hideDeployButton = true;
        hideChangeButton = true;
    }

    let modal = null;

    if (openModalType === 'requestReview') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('send-to-moderation')}
                title={t('ready-for-moderation')}
                onConfirm={requestReview}
                loading={isLoading}
            >
                <p className={styles.grayNote}>{t('moderator-comment-placeholder')}</p>
                <TextArea
                    className={styles.moderatorCommentTextfield}
                    error={settingFormErrors.noteForModerator}
                    note={
                        <span className={styles.grayNote}>
                            {lengthConfig.noteForModerator - (settingValues.noteForModerator?.length || 0)}
                        </span>
                    }
                    onChange={(e) => onSettingValuesChange('noteForModerator')(e.target.value)}
                    placeholder={t('moderator-comment-placeholder')}
                    rows={5}
                    size="xl"
                    value={settingValues.noteForModerator}
                />
            </ModalWithConfirmation>
        );
    }
    if (openModalType === 'cancelReview') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('change')}
                title={t('changing-data-will-stop-moderation')}
                text={t('if-you-want-to-change-the-data')}
                onConfirm={cancelReview}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'changeToDraft') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('go-to-draft')}
                title={t('change-in-copy')}
                text={t('you-can-not-change-published-version')}
                onConfirm={() => setIsDraft(true, aliceApp.channel)}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'deleteApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('delete')}
                text={t('delete-text')}
                title={t('delete-title')}
                onConfirm={deleteSkill}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'stopApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('stop')}
                text={t('stop-text')}
                title={t('stop-title')}
                onConfirm={stopSkill}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'forceStopApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('stop')}
                title={t('stop-title')}
                text={t('stop-text')}
                onConfirm={() => forceStopSkill(forceStopComment)}
                loading={isLoading}
                confirmDisabled={!forceStopComment}
            >
                <TextArea
                    className={styles.moderatorCommentTextfield}
                    error={settingFormErrors.noteForModerator}
                    note={
                        <span className={styles.grayNote}>
                            {lengthConfig.noteForModerator - (settingValues.noteForModerator?.length || 0)}
                        </span>
                    }
                    onChange={(e) => setForceStopComment(e.target.value)}
                    placeholder={t('force-stop-not-empty-comment')}
                    rows={5}
                    size="xl"
                    value={forceStopComment}
                    errorMessage={!forceStopComment && t('force-stop-not-empty-comment')}
                />
            </ModalWithConfirmation>
        );
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.appTitle}>
                    <IconMedium imageUrl={logoUrl} />
                    <h1 className={styles.appTitle__header}>{appName}</h1>
                    <div className={styles.appTitle__mobileStatusIcon}>
                        <OperationStatus isDraft={isDraft} />
                    </div>
                </div>
                <div className={styles.buttonsContainer}>
                    <Button
                        className={clx(hideSaveButton && styles.button_hidden)}
                        disabled={hasValidationErrors}
                        loading={isSubmitting || isLoading}
                        type="submit"
                    >
                        {t('save')}
                    </Button>
                    <Button
                        className={clx(hideModerationButton && styles.button_hidden)}
                        view={aliceApp.draft.isAllowedForDeploy ? 'action' : 'normal'}
                        disabled={disableModerationButton}
                        onClick={() => setOpenModalType('requestReview')}
                        loading={isLoading}
                    >
                        {t('send-to-moderation')}
                    </Button>
                    <Button
                        className={clx(hideStopButton && styles.button_hidden)}
                        disabled={disableStopButton}
                        onClick={() => setOpenModalType(isAdmin ? 'forceStopApp' : 'stopApp')}
                        loading={isLoading}
                    >
                        <Icon data={CirclePause} />
                        {t('stop')}
                    </Button>
                    <Button
                        className={clx(hideDeployButton && styles.button_hidden)}
                        view="action"
                        onClick={requestDeploy}
                        loading={isLoading}
                    >
                        {t('action-deploy')}
                    </Button>
                    <Button
                        className={clx(hideChangeButton && styles.button_hidden)}
                        onClick={() => setOpenModalType(isDraft ? 'cancelReview' : 'changeToDraft')}
                        loading={isLoading}
                    >
                        {t('change')}
                    </Button>
                    {!disabled && (
                        <DropdownMenu
                            renderSwitcher={(props) => (
                                <Button {...props}>
                                    <Icon size={16} data={Ellipsis} />
                                </Button>
                            )}
                            items={[
                                {
                                    action: () => setOpenModalType('deleteApp'),
                                    iconStart: <Icon size={16} data={TrashBin} />,
                                    text: t('delete'),
                                    theme: 'danger',
                                },
                            ]}
                        />
                    )}
                </div>
            </div>
            <div className={clx(styles.errorsBlock, formErrors.length === 0 && styles.errorsBlock_hidden)}>
                {formErrors.map((errorMessage) => (
                    <Label key={errorMessage} className={styles.errorLabel} theme="danger">
                        {errorMessage}
                    </Label>
                ))}
            </div>
            {modal}
        </>
    );
};
